import React, { useState, useEffect, useRef } from "react";
import apiClient from "../api/apiClient";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const Home = () => {
  const [temoignages, setTemoignages] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [slides, setSlides] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [retryCount, setRetryCount] = useState(0);

  const heroCarouselRef = useRef(null);
  const testimonialsCarouselRef = useRef(null);
  const servicesCarouselRef = useRef(null);
  const mountedRef = useRef(true);

  const carouselInstances = useRef({
    heroOwl: null,
    testimonialsOwl: null,
    servicesOwl: null,
  });

  // Helper function to build image URLs
  const getImageUrl = (path, defaultPath = "images/portfolio/default.jpg") => {
    if (!path) return defaultPath;
    if (path.startsWith("http")) return path;
    if (path.startsWith("/uploads/")) {
      return `${path}`;
    }
    return path;
  };

  // Fonction pour détruire de manière sécurisée les carousels
  const safeDestroyCarousel = (carouselInstance, refElement) => {
    try {
      if (
        carouselInstance &&
        typeof window !== "undefined" &&
        window.$ &&
        refElement
      ) {
        // Vérifier si l'élément existe encore dans le DOM
        if (document.contains(refElement)) {
          // Arrêter l'autoplay avant de détruire
          carouselInstance.trigger("stop.owl.autoplay");
          // Détruire le carousel
          carouselInstance.trigger("destroy.owl.carousel");
          // Nettoyer les classes ajoutées par Owl Carousel
          window.$(refElement).removeClass("owl-loaded owl-drag");
          window.$(refElement).find(".owl-stage-outer").contents().unwrap();
          window.$(refElement).find(".owl-stage").contents().unwrap();
          window.$(refElement).find(".owl-item").contents().unwrap();
        }
      }
    } catch (error) {}
  };

  // Fonction pour détruire tous les carousels
  const destroyAllCarousels = () => {
    if (!mountedRef.current) return;
    safeDestroyCarousel(
      carouselInstances.current.heroOwl,
      heroCarouselRef.current,
    );
    safeDestroyCarousel(
      carouselInstances.current.testimonialsOwl,
      testimonialsCarouselRef.current,
    );
    safeDestroyCarousel(
      carouselInstances.current.servicesOwl,
      servicesCarouselRef.current,
    );
    carouselInstances.current = {
      heroOwl: null,
      testimonialsOwl: null,
      servicesOwl: null,
    };
  };

  // Fonction pour initialiser les carousels de manière sécurisée
  const initializeCarousels = () => {
    if (
      !mountedRef.current ||
      typeof window === "undefined" ||
      !window.$ ||
      !window.$.fn.owlCarousel
    ) {
      return;
    }
    try {
      // Détruire les instances existantes d'abord
      destroyAllCarousels();
      // Attendre un tick pour que le DOM soit stable
      setTimeout(() => {
        if (!mountedRef.current) return;

        // Initialiser le carousel hero
        if (heroCarouselRef.current && slides.length > 0) {
          try {
            const $heroElement = window.$(heroCarouselRef.current);
            if ($heroElement.length && !$heroElement.hasClass("owl-loaded")) {
              carouselInstances.current.heroOwl = $heroElement.owlCarousel({
                items: 1,
                loop: slides.length > 1,
                autoplay: slides.length > 1,
                autoplayTimeout: 5000,
                nav: true,
                dots: true,
                animateOut: "fadeOut",
                animateIn: "fadeIn",
                smartSpeed: 450,
                autoplayHoverPause: true,
              });
            }
          } catch (error) {}
        }

        // Initialiser le carousel témoignages
        if (testimonialsCarouselRef.current && temoignages.length > 0) {
          try {
            const $testimonialsElement = window.$(
              testimonialsCarouselRef.current,
            );
            if (
              $testimonialsElement.length &&
              !$testimonialsElement.hasClass("owl-loaded")
            ) {
              carouselInstances.current.testimonialsOwl =
                $testimonialsElement.owlCarousel({
                  items: 1,
                  loop: temoignages.length > 1,
                  autoplay: temoignages.length > 1,
                  autoplayTimeout: 4000,
                  nav: false,
                  dots: true,
                  smartSpeed: 450,
                  autoplayHoverPause: true,
                });
            }
          } catch (error) {}
        }

        // Initialiser le carousel services
        if (servicesCarouselRef.current && services.length > 0) {
          try {
            const $servicesElement = window.$(servicesCarouselRef.current);
            if (
              $servicesElement.length &&
              !$servicesElement.hasClass("owl-loaded")
            ) {
              carouselInstances.current.servicesOwl =
                $servicesElement.owlCarousel({
                  loop: services.length > 3,
                  autoplay: services.length > 1,
                  autoplayTimeout: 3000,
                  nav: true,
                  dots: false,
                  smartSpeed: 450,
                  autoplayHoverPause: true,
                  responsive: {
                    0: { items: 1 },
                    768: { items: 2 },
                    992: { items: 3 },
                  },
                });
            }
          } catch (error) {}
        }
      }, 200);
    } catch (error) {}
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrors({});
      const endpoints = [
        { key: "temoignages", url: `/temoignages` },
        { key: "sponsors", url: `/sponsors` },
        { key: "slides", url: `/sliders` },
        { key: "blogs", url: `/blogs` },
        { key: "services", url: `/services` },
      ];

      const requests = endpoints.map((endpoint) =>
        apiClient
          .get(endpoint.url, { timeout: 15000 })
          .then((response) => ({
            key: endpoint.key,
            data: response.data,
            error: null,
          }))
          .catch((error) => ({
            key: endpoint.key,
            data: null,
            error: error.response
              ? `Erreur ${error.response.status}: ${
                  error.response.data?.message || "Erreur serveur"
                }`
              : error.request
                ? "Erreur réseau: Impossible de contacter le serveur"
                : "Erreur inconnue",
          })),
      );

      const results = await Promise.all(requests);
      const newErrors = {};
      results.forEach((result) => {
        if (result.error) {
          newErrors[result.key] = result.error;
        } else {
          switch (result.key) {
            case "temoignages":
              setTemoignages(Array.isArray(result.data) ? result.data : []);
              break;
            case "sponsors":
              setSponsors(Array.isArray(result.data) ? result.data : []);
              break;
            case "slides":
              setSlides(Array.isArray(result.data) ? result.data : []);
              break;
            case "blogs":
              setBlogs(Array.isArray(result.data) ? result.data : []);
              break;
            case "services":
              setServices(Array.isArray(result.data) ? result.data : []);
              break;
            default:
              break;
          }
        }
      });
      setErrors(newErrors);
    } catch (error) {
      setErrors({ general: "Erreur générale lors du chargement des données" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [retryCount]);

  // Initialiser les carousels une fois que les données sont chargées
  useEffect(() => {
    if (!loading && mountedRef.current) {
      const timer = setTimeout(() => {
        if (mountedRef.current) {
          initializeCarousels();
        }
      }, 300);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [loading, slides.length, temoignages.length, services.length]);

  // Nettoyer lors du démontage
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      destroyAllCarousels();
    };
  }, []);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  // Show full page loading if (loading && retryCount === 0)
  if (loading && retryCount === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement de la page d'accueil...</p>
        </div>
      </div>
    );
  }

  // Show full page error if all endpoints failed
  if (Object.keys(errors).length === 5 && !loading) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-danger mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
                <h4>Erreur de chargement</h4>
                <p className="mb-3">
                  Impossible de charger les données de la page d'accueil.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={handleRetry}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Chargement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo me-2"></i> Réessayer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>DVELGE GROUP | Solutions Digitales Innovantes</title>
        <meta
          name="description"
          content="DVELGE GROUP - Votre partenaire pour la transformation digitale, développement de logiciels, formation IT et fourniture de solutions technologiques en Guinée."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="DVELGE GROUP | Solutions Digitales Innovantes"
        />
        <meta
          property="og:description"
          content="Découvrez nos services de développement logiciel, formation IT et solutions technologiques pour votre entreprise."
        />
        <meta property="og:url" content="https://www.dvelgegroup.com" />
        <meta
          property="og:image"
          content="https://www.dvelgegroup.com/images/logo.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="DVELGE GROUP | Solutions Digitales Innovantes"
        />
        <meta
          name="twitter:description"
          content="Découvrez nos services de développement logiciel, formation IT et solutions technologiques pour votre entreprise."
        />
        <meta
          name="twitter:image"
          content="https://www.dvelgegroup.com/images/logo.png"
        />
      </Helmet>
      {/* Global retry button if multiple sections failed */}
      {Object.keys(errors).length > 0 && (
        <div className="container mt-3">
          <div className="alert alert-warning text-center" role="alert">
            <small>
              <i className="fas fa-exclamation-triangle me-2"></i> Certaines
              données n'ont pas pu être chargées.
              <button
                className="btn btn-sm btn-outline-primary ms-2"
                onClick={handleRetry}
                disabled={loading}
              >
                {loading ? "Chargement..." : "Réessayer"}
              </button>
            </small>
          </div>
        </div>
      )}
      {/* SECTION SLIDES */}
      <section className="home-hero slide-hero">
        {errors.slides ? (
          <div
            className="slide owlbg11"
            style={{ backgroundImage: "url(images/default-slide.jpg)" }}
          >
            <div className="container">
              <div className="row">
                <div className="col-md-6 col-sm-12 vcenter">
                  <div className="hero-content-x">
                    <h1 className="mb30">
                      <span className="ree-tt">
                        {" "}
                        Bienvenue chez DVELGE GROUP{" "}
                      </span>
                    </h1>
                    <p>
                      {" "}
                      Votre partenaire de confiance pour la transformation
                      digitale{" "}
                    </p>
                    <Link
                      to="/contacts"
                      className="ree-btn ree-btn-grdt1 mt40 mw-80"
                    >
                      Contactez-nous{" "}
                      <i className="fas fa-arrow-right fa-btn"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : slides.length > 0 ? (
          <div ref={heroCarouselRef} className="hero-owl owl-carousel">
            {slides.map((slide) => (
              <div
                key={slide.id_slider}
                className="slide owlbg11"
                style={{
                  backgroundImage: `url(${getImageUrl(
                    slide.image_slider,
                    "images/default-slide.jpg",
                  )})`,
                }}
              >
                <div className="container">
                  <div className="row">
                    <div className="col-md-6 col-sm-12 vcenter">
                      <div className="hero-content-x">
                        <h1
                          className="mb30"
                          data-aos="fade-in"
                          data-aos-delay="200"
                        >
                          <span className="ree-tt">{slide.titre_slider}</span>
                        </h1>
                        <p data-aos="fade-in" data-aos-delay="500">
                          {slide.description_slider}
                        </p>
                        <Link
                          to="/contacts"
                          className="ree-btn ree-btn-grdt1 mt40 mw-80"
                          data-aos="fade-in"
                          data-aos-delay="800"
                        >
                          Contactez-nous{" "}
                          <i className="fas fa-arrow-right fa-btn"></i>
                        </Link>
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-12 vcenter">
                      <div className="sol-image m-mt30">
                        <img
                          src={getImageUrl(slide.image_path)}
                          alt={slide.titre_slider}
                          className="img-fluid"
                          data-aos="fade-in"
                          data-aos-delay="400"
                          onError={(e) => {
                            e.target.src = "images/portfolio/default.jpg";
                            e.target.alt = "Image non disponible";
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="slide owlbg11"
            style={{ backgroundImage: "url(images/default-slide.jpg)" }}
          >
            <div className="container">
              <div className="row">
                <div className="col-md-6 col-sm-12 vcenter">
                  <div className="hero-content-x">
                    <h1 className="mb30">
                      <span className="ree-tt">
                        {" "}
                        Bienvenue chez DVELGE GROUP{" "}
                      </span>
                    </h1>
                    <p>
                      {" "}
                      Votre partenaire de confiance pour la transformation
                      digitale{" "}
                    </p>
                    <Link
                      to="/contacts"
                      className="ree-btn ree-btn-grdt1 mt40 mw-80"
                    >
                      Contactez-nous{" "}
                      <i className="fas fa-arrow-right fa-btn"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
      {/* SECTION SPONSORS */}
      <section className="home-about sec-pad r-bg-a">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="about-content-home m-mb60 pera-block text-center">
                <h2>
                  <span className="ree-tt">Collaborateurs </span>
                </h2>
                <p>
                  Nous sommes fiers de construire des relations solides avec nos
                  collaborateurs de confiance, dont le soutien et la coopération
                  contribuent à notre succès mutuel.
                </p>
                {errors.sponsors && (
                  <div className="alert alert-warning mt-3" role="alert">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {errors.sponsors}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!errors.sponsors && sponsors.length > 0 ? (
            <div className="row justify-content-center">
              <div className="col-lg-10 mt60">
                <div className="client-logo-set d-flex flex-wrap justify-content-center">
                  {sponsors.map((sponsor) => (
                    <a key={sponsor.id_sponsor} href="#" className="m-2">
                      <img
                        src={getImageUrl(sponsor.image_path)}
                        alt={sponsor.nom_sponsor}
                        className="img-fluid"
                        style={{ maxHeight: "100px", maxWidth: "150px" }}
                        onError={(e) => {
                          e.target.src = "images/portfolio/default.jpg";
                          e.target.alt = "Logo non disponible";
                        }}
                      />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            !errors.sponsors && (
              <div className="row justify-content-center">
                <div className="col-lg-10 mt60">
                  <div className="text-center">
                    <i className="fas fa-handshake fa-3x text-muted mb-3"></i>
                    <p className="text-muted">
                      {" "}
                      Aucun collaborateur à afficher pour le moment.{" "}
                    </p>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </section>

      {/* SECTION SERVICES (DYNAMIQUE) */}
      <section className="r-bg-a sec-pad">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 col-sm-8 vcenter">
              <div className="heading-hz-btn">
                <h2>
                  {" "}
                  Nos <span className="ree-tt">Services</span>{" "}
                </h2>
              </div>
            </div>
            <div className="col-lg-6 col-sm-4 vcenter">
              <div className="link-sol-header">
                <Link to="/services" className="ree-card-link">
                  Voir tous <i className="fas fa-arrow-right fa-btn"></i>
                </Link>
              </div>
            </div>
          </div>

          {errors.services && (
            <div className="alert alert-warning mt-3" role="alert">
              <small>
                <i className="fas fa-exclamation-triangle me-2"></i>
                {errors.services}
              </small>
            </div>
          )}

          {!errors.services && services.length > 0 ? (
            <div className="row mt60">
              <div className="col-lg-12">
                <div
                  ref={servicesCarouselRef}
                  className="full-work-block owl-carousel"
                >
                  {services.map((service) => (
                    <div key={service.id_service} className="fwb-main-x fwb-a">
                      <div className="work-thumbnails">
                        <Link
                          to={`${service.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={getImageUrl(service.image_path)}
                            alt={service.titre}
                            className="img-fluid"
                            onError={(e) => {
                              e.target.src = "images/portfolio/default.jpg";
                              e.target.alt = "Image non disponible";
                            }}
                          />
                        </Link>
                      </div>
                      <div className="work-details">
                        <h4>
                          <Link to={`/servicesDetails/${service.id_service}`}>
                            {service.titre}
                          </Link>
                        </h4>
                        <p>{service.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            !errors.services && (
              <div className="row mt60">
                <div className="col-12 text-center">
                  <div className="text-center py-5">
                    <i className="fas fa-cogs fa-3x text-muted mb-3"></i>
                    <h4 className="text-muted">Aucun service à afficher</h4>
                    <p className="text-muted">
                      {" "}
                      Nos services seront bientôt disponibles.{" "}
                    </p>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </section>
      {/* SECTION EXPERTISE */}
      <section className="r-bg-x zup sec-pad">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-4">
              <div className="sol-img m-mt30">
                <img
                  src="images/others/wondering.jpg"
                  alt="DVELGE office"
                  className="img-fluid"
                  onError={(e) => {
                    e.target.src = "images/portfolio/default.jpg";
                    e.target.alt = "Image non disponible";
                  }}
                />
              </div>
            </div>
            <div className="col-lg-7">
              <div className="pera-block ml50">
                <h2>
                  Pourquoi choisir<span className="ree-tt"> DVELGE ?</span>
                </h2>
                <p>
                  Chez DVELGE Group, nous aidons les entreprises et institutions
                  à travers le monde à renforcer l'engagement client, à
                  optimiser leurs opérations et à bâtir la confiance grâce à la
                  technologie. Nous concevons et intégrons des solutions
                  complètes d'expérience client et de personnalisation allant de
                  la gestion de files d'attente et de rendez-vous, aux bornes
                  libre-service, affichages digitaux, systèmes de
                  personnalisation sécurisée de cartes, dématérialisation
                  documentaire et développement de logiciels d'entreprise.
                </p>
                <p>
                  Portés par l'innovation et les meilleures pratiques
                  internationales, nous combinons une expertise locale
                  approfondie et des standards mondiaux pour créer des
                  écosystèmes numériques fiables, évolutifs et prêts pour
                  l'avenir, qui accompagnent la croissance de nos partenaires
                  avec confiance.
                </p>
              </div>
            </div>
          </div>

          <div className="row mt30">
            <div className="col-lg-4 mt30">
              <div className="pera-block ml50">
                <h4>Innovation</h4>
                <p>
                  Nous développons des solutions uniques et sur mesure, en
                  combinant créativité et technologies de pointe pour répondre
                  aux défis les plus complexes
                </p>
              </div>
            </div>

            <div className="col-lg-4 mt30">
              <div className="pera-block ml50">
                <h4>Professionnalisme</h4>
                <p>
                  Grâce à des processus rigoureux et une approche méthodique,
                  nous garantissons une exécution sans faille et des résultats
                  de haute qualité.
                </p>
              </div>
            </div>

            <div className="col-lg-4 mt30">
              <div className="pera-block ml50">
                <h4>Partenariats stratégiques</h4>
                <p>
                  En collaborant avec les meilleurs fabricants et partenaires
                  technologiques internationaux, nous assurons des solutions
                  fiables et durables
                </p>
              </div>
            </div>

            <div className="col-lg-4 mt30">
              <div className="pera-block ml50">
                <h4>Scalabilité</h4>
                <p>
                  Nos solutions sont pensées pour évoluer avec vous, en
                  s'adaptant à la croissance de votre entreprise, aujourd'hui et
                  demain
                </p>
              </div>
            </div>

            <div className="col-lg-4 mt30">
              <div className="pera-block ml50">
                <h4>Engagement Total </h4>
                <p>
                  Votre succès est notre priorité. Nous bâtissons des relations
                  solides et durables, en nous engageant pleinement à livrer des
                  résultats concrets qui dépassent vos attentes.
                </p>
              </div>
            </div>

            <div className="col-lg-4 mt30">
              <div className="pera-block ml50">
                <h4>Expertise</h4>
                <p>
                  Avec une équipe multidisciplinaire d'experts ayant en moyenne
                  plus de 8 ans d'expérience, nous combinons nos compétences en
                  développement de logiciels, fourniture de technologies et
                  formation pour proposer des solutions efficaces et sur mesure,
                  adaptées aux besoins uniques de nos clients
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* SECTION TEMOIGNAGES */}
      {/* {!errors.temoignages && temoignages.length > 0 && (
        <section
          className="sec-pad bg-opcty"
          data-parallax="scroll"
          data-image-src="images/others/face-collage.png"
        >
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="heading-review text-center">
                  <h2 className="mb50">
                    Nos <span className="ree-text rt40">clients</span>{" "}
                    <span className="ree-text rt40">nous</span> félicitent pour
                    nos excellents résultats
                  </h2>
                  <div
                    ref={testimonialsCarouselRef}
                    className="testimonial-card-b owl-carousel mt30"
                  >
                    {temoignages.map((temoin) => (
                      <div className="review-slider" key={temoin.id_tem}>
                        <h4 className="mb20">"{temoin.description}"</h4>
                        <p>{temoin.profession}</p>
                        <div className="reviewers mt30">
                          <div className="reviewers-nam">
                            <h4>
                              {temoin.nom_temoin} {temoin.prenom_temoin}
                            </h4>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="clients-creative-pic mt30">
                    <div className="owl-dots" id="testimonials-avatar">
                      {temoignages.map((temoin, index) => (
                        <button
                          key={index}
                          className={`dot-c1 tcc${index + 1} owl-dot mt30`}
                        >
                          <img
                            alt={temoin.nom_temoin}
                            src={getImageUrl(temoin.image_path)}
                            onError={(e) => {
                              e.target.src = "images/portfolio/default.jpg";
                              e.target.alt = "Photo non disponible";
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )} */}
      {/* SECTION BLOGS */}
      <section className="half-bg-ree sec-pad">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="sec-heading text-center pera-block">
                <span className="sub-heading mb15">
                  Derniers articles du blog
                </span>
                <h2>
                  <span className="ree-text rt40">Lisez</span> des histoires,
                  des conseils et nos avis sur tous les sujets.
                </h2>
                <p>
                  Restez informé des dernières tendances technologiques et
                  découvrez comment votre entreprise peut tirer parti de ces
                  innovations.
                </p>
                {errors.blogs && (
                  <div className="alert alert-warning mt-3" role="alert">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {errors.blogs}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!errors.blogs && blogs.length > 0 ? (
            <div className="row mt30">
              {blogs.slice(0, 3).map((blog, index) => (
                <div
                  key={blog.id_blog}
                  className="col-lg-4 mt30"
                  data-aos="fade-in"
                  data-aos-delay={200 + index * 300}
                >
                  <div className="half-blog-card">
                    <div className="half-blog-img">
                      <Link to={`/blog-details/${blog.id_blog}`}>
                        <img
                          src={getImageUrl(blog.image_path)}
                          alt={blog.titre_blog}
                          className="img-fluid"
                          onError={(e) => {
                            e.target.src = "images/blogs/default.jpg";
                            e.target.alt = "Image non disponible";
                          }}
                        />
                      </Link>
                    </div>
                    <div className="half-blog-content">
                      <div className="blog-quick-inf mb20">
                        <span>
                          <i className="far fa-calendar-alt"></i>{" "}
                          {blog.created_at
                            ? new Date(blog.created_at).toLocaleDateString(
                                "fr-FR",
                              )
                            : "Date non disponible"}
                        </span>
                        <span>
                          <i className="fas fa-clock"></i> 5 min de lecture
                        </span>
                      </div>
                      <h4 className="mb15">
                        <Link to={`/blog-details/${blog.id_blog}`}>
                          {blog.titre_blog}
                        </Link>
                      </h4>
                      <p>
                        {blog.description_blog ||
                          "Aucune description disponible."}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !errors.blogs && (
              <div className="row mt30">
                <div className="col-12 text-center">
                  <div className="text-center py-5">
                    <i className="fas fa-newspaper fa-3x text-muted mb-3"></i>
                    <h4 className="text-muted">Aucun article disponible</h4>
                    <p className="text-muted">
                      {" "}
                      Nos articles de blog seront bientôt disponibles.{" "}
                    </p>
                    <Link to="/blogs" className="btn btn-primary">
                      {" "}
                      Voir le blog{" "}
                    </Link>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </section>
      {/* SECTION CONTACT */}
      <section className="home-contact mt110 pb100">
        <div className="container">
          <div className="row">
            <div className="cta-info-tt">
              <div className="row justify-content-between">
                <div className="col-lg-5 cinftt">
                  <div className="media">
                    <div className="ree-icon-set w80">
                      <img
                        src="images/icons/talk-with-experts.jpg"
                        alt="expert"
                        className="w-100"
                      />
                    </div>
                    <div className="ree-details-set pera-block">
                      <h4>
                        Vous souhaitez lancer votre projet dès maintenant ?
                        Parlez à nos experts.{" "}
                      </h4>
                      <Link
                        to="/contacts#demand"
                        className="ree-card-link mt30"
                      >
                        Obtenez un devis{" "}
                        <i className="fas fa-arrow-right fa-btn"></i>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="col-lg-5 cinftt">
                  <div className="media">
                    <div className="ree-icon-set w80">
                      <img
                        src="images/icons/talk-with-experts.jpg"
                        alt="expert"
                        className="w-100"
                      />
                    </div>
                    <div className="ree-details-set pera-block">
                      <h4>
                        {" "}
                        Besoin d'un site web, d'une application mobile ou d'une
                        stratégie digitale ?{" "}
                      </h4>
                      <Link
                        to="/contacts#demand"
                        className="ree-card-link mt30"
                      >
                        Faites confiance à DVELGE Group pour donner vie à vos
                        idées <i className="fas fa-arrow-right fa-btn"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="container">
            <div className="row">
              <div className="col-lg-4 mt30">
                <div className="ourinfo">
                  <p>Adresse</p>
                  <h5>
                    <Link to="https://www.google.com/maps/dir//IMMEUBLE+TAOUYA,+En+Face+Maison+Des+Jeunes,+1er+%C3%A9tage,+Ratoma,+Guinea/@9.5823474,-13.7408991,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x40414f53e4c410b3:0x7d3954ffd78224d5!2m2!1d-13.6584972!2d9.5823571?entry=ttu&g_ep=EgoyMDI1MDUxNS4wIKXMDSoJLDEwMjExNDU1SAFQAw%3D%3D">
                      1er étage, IMMEUBLE TAOUYA, En Face Maison Des Jeunes De
                      Ratoma, Conakry, Guinée
                    </Link>
                  </h5>
                </div>
              </div>

              <div className="col-lg-4 mt30">
                <div className="ourinfo">
                  <p>Courriel d'information</p>
                  <h5>
                    <a href="mailto:info@dvelgegroup.com">
                      info@dvelgegroup.com
                    </a>
                  </h5>
                </div>
              </div>

              <div className="col-lg-4 mt30">
                <div className="ourinfo">
                  <p>Courriel des Ventes</p>
                  <h5>
                    <a href="mailto:ventes@dvelgegroup.com">
                      {" "}
                      ventes@dvelgegroup.com{" "}
                    </a>
                  </h5>
                </div>
              </div>

              <div className="col-lg-4 mt30">
                <div className="ourinfo">
                  <p>Téléphone des Ventes</p>
                  <h5>
                    <a href="tel:+224623300992">+224 623 30 09 92</a>
                  </h5>
                </div>
              </div>

              <div className="col-lg-4 mt30">
                <div className="ourinfo">
                  <p>Téléphone d'information</p>
                  <h5>
                    <a href="tel:+224627020871">+224 627 02 08 71</a>
                  </h5>
                </div>
              </div>

              <div className="col-lg-4 mt30">
                <div className="ourinfo">
                  <p>Subscribe to Newsletter</p>
                  <div className="formnews">
                    <input type="email" placeholder="Email Address" />
                    <button type="submit">
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default Home;
